import { useCallback } from 'react';
import { useField, useFormikContext } from 'formik';
import { dbui_v1_TestAccessRequest } from '@antimatterhq/asl-api-ts-v1';
import { Icon } from '@antimatterhq/ui/components/icon';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { useGetExternalLocations } from '@/hooks/dbui/useGetExternalLocations';
import { FormTypeahead, FormTypeaheadProps } from '@/components/form/form';
import { useDatabricksPathValidation } from '@/components/form/input/FormDatabricksPathAndTableValidatedInput';
import { cn } from '@/lib/utils';
import { useCheckFormikError } from '@/hooks/formik/useCheckFormikError';
import ExternalLocationsTypeahead from '@/components/typeahead/externalLocations/ExternalLocationsTypeahead';
import { useAsyncValidation } from '@/contexts/AsyncValidationContext';

export interface FormDatabricksPathValidatedTypeaheadProps
  extends Omit<FormTypeaheadProps, 'items' | 'minChars' | 'value' | 'name'> {
  name: string;
  constraint: dbui_v1_TestAccessRequest['constraint'];
  debounceTime?: number;
}

export default function FormDatabricksPathValidatedTypeahead({
  name,
  constraint,
  debounceTime,
  disabled,
  className,
  ...rest
}: FormDatabricksPathValidatedTypeaheadProps) {
  const { enableApiFieldValidation } = useFlags<FlagSet>();
  const [field] = useField<string>(name);
  const { isSubmitting, setErrors } = useFormikContext();
  const { error: formikError } = useCheckFormikError({ name });
  const { data: { locations = [] } = {} } = useGetExternalLocations();

  const { setError, clearError, setPending } = useAsyncValidation();

  const {
    isPending,
    isValid,
    error: validationError,
  } = useDatabricksPathValidation({
    value: enableApiFieldValidation ? field.value : '',
    constraint,
    validationType: 'path',
    debounceTime,
    disabled: disabled || isSubmitting,
    onError: useCallback(
      (error: unknown) => {
        if (enableApiFieldValidation) {
          setErrors({ [name]: error });

          if (error) {
            setError(name, error as string);
          } else {
            clearError(name);
          }
        }
      },
      [name, setErrors, enableApiFieldValidation, setError, clearError],
    ),
    onPendingChange: useCallback(
      (isPendingState: boolean) => {
        if (enableApiFieldValidation) {
          setPending(name, isPendingState);
        }
      },
      [enableApiFieldValidation, name, setPending],
    ),
  });

  // If feature flag is off, just render the regular typeahead
  if (!enableApiFieldValidation) {
    return (
      <ExternalLocationsTypeahead
        name={name}
        disabled={disabled}
        className={className}
        {...rest}
      />
    );
  }

  const hasError =
    !!formikError ||
    (validationError !== null && !isPending) ||
    (isValid === false && !isPending);
  const isEffectivelyDisabled = disabled || isSubmitting;

  return (
    <div className="relative w-full max-w-form">
      <FormTypeahead
        name={name}
        items={locations?.map((location) => location.url) || []}
        minChars={0}
        disabled={isEffectivelyDisabled}
        className={cn('max-w-form pr-10', className)}
        variant={hasError ? 'error' : 'default'}
        {...rest}
      />
      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        {isPending && (
          <Icon
            icon="lucide:loader-circle"
            className="h-4 w-4 animate-spin text-gray-400"
          />
        )}
        {isValid === true && !isPending && !formikError && (
          <Icon
            icon="lucide:check-circle"
            className={cn(
              'h-4 w-4 text-green-500',
              isEffectivelyDisabled ? 'opacity-50' : '',
            )}
          />
        )}
        {hasError && !isPending && (
          <Icon
            icon="lucide:octagon-alert"
            className={cn(
              'h-4 w-4',
              'text-red-500',
              isEffectivelyDisabled ? 'opacity-50' : '',
            )}
          />
        )}
      </div>
    </div>
  );
}
